import React, { useState, useRef, useEffect } from 'react'

import { connect } from 'react-redux'
import yup from 'yup'

import Form from 'components/form/form'
import copy from 'fast-copy'

import {
    loginUser,
    saveUser,
    changeTitle,
    clearPending,
} from 'state/actions'

import {
    getCurrentUser,
    getCurrentAccount,
} from 'state/selectors'

import { logAnalyticsEvent } from 'utils/analytics'
import { getUrlParams } from 'utils/text'

const User = ({
    setStep,
    isActiveStep,
    saveUser,
    invitation,
    refreshSlider,
    clearPending,
    userData,
    setLoginCredentials,
    handleBackToLogin,
    handleGoToDashboard,
    loginUser,
    invitationError
}) => {
    const [formValues, setFormValues] = useState({})
    const [isLoading, setIsLoading] = useState(false)
    const [formError, setFormError] = useState(null)

    const isGuest = invitation && invitation.is_guest

    const currentUserRef = useRef(userData)
    const formValuesRef = useRef(formValues)
    const isActiveStepRef = useRef(isActiveStep)

    formValuesRef.current = formValues
    isActiveStepRef.current = isActiveStep
    currentUserRef.current = userData

    const formRows = [
        [
            {
                placeholder: 'First name',
                key: 'first_name',
                type: 'text',
            }, {
                placeholder: 'Last name',
                key: 'last_name',
                type: 'text',
            }
        ], [
            {
                placeholder: 'Email address',
                key: 'email',
                type: 'email',
                disabled: !!invitation
            }
        ], [
            {
                placeholder: `What's your role?`,
                key: 'role',
                type: 'text',
                hidden: isGuest
            }
        ], [
            {
                placeholder: 'Choose a password',
                key: 'password',
                type: 'password',
                hidden: !!formValues.id
            }
        ], [
            {
                type: 'checkbox',
                key: 'agree_terms',
                props: {
                    label: `I've read and agree to the <a href="#">terms of use</a> and <a href="#">privacy policy</a>.`
                }
            }
        ]
    ]

    const userId = userData ? userData.id : null

    useEffect(() => {
        // If it's during registration, scoot 'em across!
        if (userData && userData.id) {
            setFormValues({
                ...userData,
                agree_terms: true
            })
        }
    }, [userId])

    useEffect(() => {
        if (invitation) {
            setFormValues({
                email: invitation.recipient_email
            })
        } else {
            const email = getUrlParams('email')

            if (email) {
                setFormValues({
                    email
                })
            }
        }
    }, [])

    useEffect(() => {
        if (invitationError) {
            switch (invitationError) {
                case 'TAKEN':
                    handleError(`This invitation has already been accepted.`, true)
                    break
                case 'NONE':
                    handleError(`This invitation appears to be missing.`, true)
                    break
            }
        }
    }, [invitationError])

    const handleError = (message, pin) => {
        setFormError(message)

        setTimeout(() => {
            refreshSlider()
        }, 350)

        if (!pin) {
            setTimeout(() => {
                setFormError(null)

                setTimeout(() => {
                    refreshSlider()
                }, 350)
            }, 3200)
        }
    }

    const handleSubmit = async e => {
        e.preventDefault()

        const _formValues = formValuesRef.current

        const isNew = !_formValues.id

        // New users schema
        let validateParams = {
            email: yup
                .string()
                .email(`That email doesn't seem to be quite right.`)
                .required(`What's your email?`),
            last_name: yup.string().required(`What's your last name?`),
            first_name: yup.string().required(`So, what's your name?`),
        }

        // Only set password here if you're new
        if (isNew) {
            validateParams.password = yup.string().min(6, 'Your password needs to be at least 6 characters.').required()
        }

        // guest invites don't need a role
        if (!isGuest) {
            validateParams.role = yup.string().required('Please enter your role.')
        }

        const userSchema = yup.object().shape(validateParams)

        // Validate input fields against schema
        try {
            await userSchema.validate({ ..._formValues })
        } catch (err) {
            return handleError(err.message)
        }

        const hasAcceptedTerms = !!_formValues.agree_terms

        if (!hasAcceptedTerms && isNew) {
            return handleError(`Please accept the Terms of Use and Privacy policy.`)
        }

        let requestData = {
            ..._formValues,
        }

        if (invitation) {
            requestData.invitation_id = invitation.id
            requestData.account_id = invitation.account_id
        }

        setIsLoading(true)

        saveUser(requestData, response => {
            if (!response) {
                alert('Log out???')
                return
            }

            if (response.data === 'TAKEN') {
                alert('Invitation as been taken already!')
            } else if (response.status && response.status == 422) {
                // local and prod seem to do this differently...?!
                let firstKey
                let errorsObject = null

                if (response.data.errors) {
                    errorsObject = response.data.errors
                } else {
                    errorsObject = response.data
                }

                firstKey = Object.keys(errorsObject)[0]

                handleError(errorsObject[firstKey][0])

                setIsLoading(false)
            } else {
                if (setLoginCredentials) {
                    setLoginCredentials(_formValues.email, _formValues.password)
                }

                logAnalyticsEvent({
                    category: 'Registration',
                    action: 'Completed Step',
                    label: '1 - User Details'
                })

                setTimeout(_ => {
                    if (invitation) {
                        clearPending()

                        loginUser(_formValues, _ => {
                            if (currentUserRef.current) {
                                handleGoToDashboard()
                            }
                        })
                    } else {
                        const shouldGoToAcceptForm = (response.data.authorisedAccount || response.data.invitedAccount)

                        setIsLoading(false)

                        setStep(shouldGoToAcceptForm ? 1 : 2)

                        if (isNew && currentUserRef.current) {
                            setFormValues({
                                ..._formValues,
                                id: currentUserRef.current.id
                            })
                        }
                    }
                }, 1000)
            }
        })
    }

    const handleChange = (key, value) => {
        const _formValues = copy(formValues)

        _formValues[key] = value

        setFormValues(_formValues)
    }

    return (
        <React.Fragment>
            <Form
                rows={formRows}
                values={formValues}
                handleSubmit={handleSubmit}
                handleChange={handleChange}
                isLoading={isLoading}
                error={formError}
                isFocused={isActiveStep}
                buttonPrimary={{
                    label: 'Save details',
                    icon: 'tick'
                }}
                buttonLow={{
                    label: `Back to login?`,
                    callback: handleBackToLogin
                }}
            />
        </React.Fragment>
    )
}

const mapStateToProps = state => {
    return {
        userData: getCurrentUser(state, true),
        accountData: getCurrentAccount(state),
    }
}

export default connect(mapStateToProps, {
    saveUser,
    loginUser,
    changeTitle,
    clearPending,
})(User)


// WEBPACK FOOTER //
// ./src/components/auth/register-modal/user.jsx